import React, { useEffect, useState, useRef } from "react";
import Loading from "../../components/Loading";
import useStore from "../../utils/store";
import { getAllDispatch, orderDispatchAdmin, getChallanOrders, createChallanApi } from "../../utils/api";
import { useParams } from "react-router-dom";
import { autoConnectAndPrint, buildChallanEscPos, initBluetoothBackground } from "../../utils/bluetoothService";

const Challan = () => {
  const [dash, setDash] = useState(null);
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [isBtPrinting, setIsBtPrinting] = useState(false);
  const [btStatus, setBtStatus] = useState("");
  const [printerName, setPrinterName] = useState("");
  const startDate = useStore((state) => state.startDate);
  const endDate = useStore((state) => state.endDate);
  const hasAutoPrinted = useRef(false);

  const getData = async () => {
    setLoading(true);
    try {
      let currentOrder = null;

      let [success, data] = await getAllDispatch(startDate, endDate);
      if (success && Array.isArray(data)) {
        for (const it of data) {
          const found = it.orders?.find((o) => String(o._id) === String(params.order) || String(o.id) === String(params.order));
          if (found) { currentOrder = found; break; }
        }
      }

      if (!currentOrder) {
        const [allSuccess, allData] = await getAllDispatch();
        if (allSuccess && Array.isArray(allData)) {
          for (const it of allData) {
            const found = it.orders?.find((o) => String(o._id) === String(params.order) || String(o.id) === String(params.order));
            if (found) { currentOrder = found; break; }
          }
        }
      }

      if (!currentOrder) {
        const [challanSuccess, challanData] = await getChallanOrders(startDate, endDate);
        if (challanSuccess && Array.isArray(challanData)) {
          for (const it of challanData) {
            const found = it.orders?.find((o) => String(o._id) === String(params.order) || String(o.id) === String(params.order));
            if (found) { currentOrder = found; break; }
          }
        }
      }

      if (!currentOrder) {
        const [adminSuccess, adminData] = await orderDispatchAdmin(startDate, endDate);
        if (adminSuccess && Array.isArray(adminData)) {
          for (const it of adminData) {
            const found = it.orders?.find((o) => String(o._id) === String(params.order) || String(o.id) === String(params.order));
            if (found) { currentOrder = found; break; }
          }
        }
      }

      if (!currentOrder) {
        const [adminSuccess2, adminData2] = await orderDispatchAdmin();
        if (adminSuccess2 && Array.isArray(adminData2)) {
          for (const it of adminData2) {
            const found = it.orders?.find((o) => String(o._id) === String(params.order) || String(o.id) === String(params.order));
            if (found) { currentOrder = found; break; }
          }
        }
      }

      setDash(currentOrder || null);
      if (currentOrder?._id) {
        createChallanApi([currentOrder._id]).catch(console.error);
      }
    } catch (e) {
      console.error("Error fetching challan data:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    initBluetoothBackground().then((res) => {
      if (res?.device) setPrinterName(res.device.name || "Printer Connected");
    });
  }, [startDate, endDate, params.id, params.order]);

  useEffect(() => {
    if (dash && !loading && !hasAutoPrinted.current) {
      hasAutoPrinted.current = true;
      setTimeout(() => {
        handlePrint();
      }, 500);
    }
  }, [dash, loading]);

  const handlePrint = async () => {
    setIsBtPrinting(true);
    try {
      const printData = buildChallanEscPos(dash, params?.weight, "challan");
      const dev = await autoConnectAndPrint(printData, (st) => setBtStatus(st));
      setPrinterName(dev?.name || "Printer");
      setBtStatus("✅ Printed Successfully!");
      setTimeout(() => setBtStatus(""), 3000);
    } catch (err) {
      console.error("Print error:", err);
      setBtStatus("❌ " + (err?.message || "Bluetooth error"));
    } finally {
      setIsBtPrinting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!dash) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-6 rounded-2xl shadow-xl text-center max-w-sm w-full border border-gray-200">
          <div className="text-4xl mb-3">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Challan Details Not Found</h2>
          <p className="text-sm text-gray-500 mb-4">
            Order ID: <span className="font-mono font-semibold text-gray-700">{params.order || params.id}</span>
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={getData}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 px-4 rounded-xl shadow transition active:scale-95 cursor-pointer"
            >
              🔄 Retry Loading
            </button>
            <button
              onClick={() => window.history.back()}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-xl transition cursor-pointer"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const dateStr = dash?.updatedAt
    ? new Date(dash.updatedAt).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center pb-24">
      {/* Top Action Bar */}
      <div className="w-full max-w-md p-3 bg-white shadow-md flex flex-col gap-2 sticky top-0 z-50 print:hidden">
        <button
          onClick={handlePrint}
          disabled={isBtPrinting}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black py-3.5 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 text-base transition-all active:scale-95 cursor-pointer"
        >
          <span className="text-xl">🖨️</span>
          <span>{isBtPrinting ? "Auto-Connecting & Printing..." : "⚡ Print Challan (Bluetooth)"}</span>
        </button>

        {btStatus && (
          <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 text-xs font-bold py-1.5 px-3 rounded-lg text-center animate-pulse">
            {btStatus}
          </div>
        )}

        <div className="flex items-center justify-between text-[11px] text-gray-500 px-1">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            {printerName ? `Auto-connected: ${printerName}` : "Auto-connect ready"}
          </span>
          <button
            onClick={handlePrint}
            className="text-emerald-700 underline font-semibold cursor-pointer"
          >
            Pair / Reconnect
          </button>
        </div>
      </div>

      {/* 58mm Thermal Slip Layout */}
      <div className="w-full flex justify-center py-4">
        <div
          id="printable-slip"
          className="bg-white text-black p-4 border border-gray-300 shadow-xl w-[320px] max-w-full font-sans"
        >
          {/* Slip 1 */}
          <div className="flex flex-col items-center text-center gap-1.5 py-1">
            <h1 className="text-xl font-black tracking-tight leading-none">PRABHU LABELS</h1>
            <div className="bg-black text-white text-[11px] font-black tracking-widest px-2 py-0.5 rounded my-0.5">
              DISPATCH CHALLAN
            </div>
            <div className="text-base font-black leading-snug">
              {dash?.customer?.userName} <span className="font-bold text-xs">({dash?.group})</span>
            </div>
            <div className="text-xs font-bold text-gray-800">Date: {dateStr}</div>
            <div className="w-full flex justify-between items-center text-sm font-bold border-t-2 border-b-2 border-black py-1.5 my-1">
              <span className="font-extrabold">{dash?.product?.productName}</span>
              <span className="text-lg font-black">{dash?.quantity}</span>
            </div>
            <div className="text-sm font-black">Weight: {params?.weight || 0} kg</div>
            <div className="text-[11px] text-gray-800 font-bold mt-0.5">Call for query: 9417771111</div>
          </div>

          {/* Cut Line */}
          <div className="my-5 border-b border-dashed border-black relative text-center">
            <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-white px-2 text-[9px] text-gray-500 font-bold">
              ✂ CUT HERE
            </span>
          </div>

          {/* Slip 2 */}
          <div className="flex flex-col items-center text-center gap-1.5 py-1">
            <h1 className="text-xl font-black tracking-tight leading-none">PRABHU LABELS</h1>
            <div className="bg-black text-white text-[11px] font-black tracking-widest px-2 py-0.5 rounded my-0.5">
              DISPATCH CHALLAN
            </div>
            <div className="text-base font-black leading-snug">
              {dash?.customer?.userName} <span className="font-bold text-xs">({dash?.group})</span>
            </div>
            <div className="text-xs font-bold text-gray-800">Date: {dateStr}</div>
            <div className="w-full flex justify-between items-center text-sm font-bold border-t-2 border-b-2 border-black py-1.5 my-1">
              <span className="font-extrabold">{dash?.product?.productName}</span>
              <span className="text-lg font-black">{dash?.quantity}</span>
            </div>
            <div className="text-sm font-black">Weight: {params?.weight || 0} kg</div>
            <div className="text-[11px] text-gray-800 font-bold mt-0.5">Call for query: 9417771111</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Challan;
